import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  isDevMode,
  OnDestroy,
} from '@angular/core';
import {Store} from '@ngrx/store';
import {selectFilterType} from '../../service/paper-chat.selector';
import {of, Subscription} from 'rxjs';
import {selectSelectedProject} from '../../../project/service/project.selectors';
import {MatDialog} from '@angular/material/dialog';
import {PaperChatSettingDialogComponent} from '../paper-chat-setting-dialog/paper-chat-setting-dialog.component';
import {ClickEventService} from '../../../../utils/services/click-event.service';
import {MatToolbar} from "@angular/material/toolbar";
import {MatAnchor} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {RouterLink} from '@angular/router';
import {TourAnchorMatMenuDirective} from 'ngx-ui-tour-md-menu';
import {loadHasAllPaperEmbedding} from '../../service/embedding.actions';
import {
  selectPaperHasEmbeddingsCount,
  selectPaperHasEmbeddingsTotal,
  selectQueryPaperHasEmbedding
} from '../../service/embedding.selectors';
import {selectRoutePaperByProjectId} from '../../../research/services/research-paper/research-paper.selectors';
import {ResearchPaperDTO} from '../../../research/dto/research-paper';
import * as ResearchPaperActions from '../../../research/services/research-paper/research-paper.actions';
import {PaperChatFilterComponent} from '../paper-chat-filter/paper-chat-filter.component';
import {PaperHasEmbeddingDTO} from '../../dto/embedding';
import {MatTooltip} from '@angular/material/tooltip';
import {PaperChatComponent} from '../paper-chat/paper-chat.component';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs/operators';

@Component({
  selector: 'app-paper-chat-window',
  imports: [
    MatToolbar,
    MatAnchor,
    MatIcon,
    RouterLink,
    TourAnchorMatMenuDirective,
    PaperChatFilterComponent,
    MatTooltip,
    PaperChatComponent
  ],
  templateUrl: './paper-chat-window.component.html',
  styleUrl: './paper-chat-window.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaperChatWindowComponent implements OnDestroy, AfterViewInit {
  private readonly store: Store = inject(Store);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly clickEventService: ClickEventService = inject(ClickEventService);


  readonly project = this.store.selectSignal(selectSelectedProject);
  readonly projectId = computed(() => this.project()?.id);
  readonly filterType = this.store.selectSignal(selectFilterType());

  readonly onlyPaper = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId
        ? this.store.select(selectRoutePaperByProjectId(projectId))
        : of(undefined as ResearchPaperDTO | undefined)
      )
    ),
    {initialValue: undefined as ResearchPaperDTO | undefined}
  );

  readonly onlyPaperHasEmbedding = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId
        ? this.store.select(selectQueryPaperHasEmbedding(projectId))
        : of(undefined as PaperHasEmbeddingDTO | undefined)
      )
    ),
    {initialValue: undefined as PaperHasEmbeddingDTO | undefined}
  );

  readonly hasEmbeddingCount = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectPaperHasEmbeddingsCount(projectId)) : of(undefined))
    ),
    {initialValue: undefined as number | undefined}
  );

  readonly hasEmbeddingTotalCount = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectPaperHasEmbeddingsTotal(projectId)) : of(undefined))
    ),
    {initialValue: undefined as number | undefined}
  );


  private clickEventSubscription?: Subscription;

  constructor() {
    effect(() => {
      const projectId = this.projectId();
      if (!projectId) return;
      this.store.dispatch(ResearchPaperActions.loadResearchPapers({projectId}));
      this.store.dispatch(loadHasAllPaperEmbedding({
        projectId
      }));
    });

    effect(() => {
      const projectId = this.projectId();
      if (!isDevMode() || !projectId) return;
      this.store.dispatch(loadHasAllPaperEmbedding({
        projectId, filter: this.filterType()
      }));
    });

    this.clickEventSubscription = this.clickEventService.clickSettingEvent$.subscribe(() => {
      this.openChatSettingsDialog();
    });

  }

  ngAfterViewInit() {
  }

  ngOnDestroy(): void {
    if (this.clickEventSubscription) {
      this.clickEventSubscription.unsubscribe();
    }
  }

  isDev(): boolean {
    return isDevMode();
  }

  private openChatSettingsDialog(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    this.dialog.open(PaperChatSettingDialogComponent, {
      data: {
        projectId
      },
      width: "400px"
    });
  }

}
